<template>
	<h3 class="h6 mb-5" :class="step === 1 ? 'mt-4' : ''" data-testid="heading">Booking Summary</h3>
	<div
		class="booking-summary row"
		:class="step === 1 ? 'mb-3 mb-lg-4' : ''"
		aria-live="polite"
		data-testid="row"
	>
		<div class="col d-sm-flex">
			<div class="booking-summary-item">
				<h4 class="h6 booking-summary-heading">
					Date
					<span class="visually-hidden">selected:</span>
				</h4>
				<span class="small" data-testid="date">
					{{ formatDate(day, 'M/D/YYYY') }}
				</span>
			</div>
			<div class="booking-summary-item">
				<h4 class="h6 booking-summary-heading">
					Time
					<span class="visually-hidden">selected:</span>
				</h4>
				<span class="small" data-testid="time">{{ displayTime }}</span>
			</div>
		</div>
		<div class="w-100 d-md-none d-lg-block d-xxl-none"></div>
		<div class="booking-summary-btn-col col">
			<button
				v-if="step === 1"
				class="btn btn-dark btn-sm booking-summary-btn"
				:disabled="!timeSlot"
				type="button"
				data-testid="next-step"
				@click="nextStep"
			>
				Continue to Booking
			</button>
			<button
				v-if="step === 2"
				class="btn btn-outline-dark btn-sm booking-summary-btn"
				type="button"
				data-testid="prev-step"
				@click="prevStep"
			>
				Edit Your Booking
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import dayjs, { type Dayjs } from 'dayjs';
import { useDates } from '@/composables/use-dates';
import type { ITimeSlot } from 'Types/aemui/schedule-a-tour/scheduleATour';
const { formatDate } = useDates();

const props = defineProps({
	step: {
		type: [Number, String] as PropType<1 | 2 | 'Confirm'>,
		required: true
	},
	day: {
		type: Object as PropType<Dayjs>,
		default: () => dayjs()
	},
	timeSlot: {
		type: Object as PropType<ITimeSlot | null>,
		default: null
	}
});

const emit = defineEmits<{
	(e: 'update:step', value: 1 | 2): void;
}>();

const displayTime = computed(() => {
	if (!props.timeSlot?.startTime) {
		return 'Select Time';
	}
	return formatDate(props.timeSlot.startTime, 'h:mmA z', props.timeSlot.timeZone);
});

const nextStep = () => {
	emit('update:step', 2);
};

const prevStep = () => {
	emit('update:step', 1);
};
</script>

<style lang="scss" scoped>
.booking-summary {
	--kpl-planner-border-color: rgba(49, 49, 49, 0.15);
	&.row {
		margin-top: 2.5rem;
		margin-bottom: 2rem;

		@include media-breakpoint-up(lg) {
			margin-top: 5rem;
		}
	}

	&-item {
		padding: 0.5rem 0;
		border-bottom: solid 1px var(--kpl-planner-border-color);

		&:last-child {
			padding-bottom: 0;
			border-bottom: none;
		}

		@include media-breakpoint-up(sm) {
			padding: 0.5rem 1rem;
			border-bottom: none;
			border-right: solid 1px var(--kpl-planner-border-color);

			&:first-child {
				padding-left: 0;
			}

			&:last-child {
				padding-right: 0;
				border-right: none;
			}
		}
	}

	&-heading {
		@include font-size(sm);
		margin-bottom: 0.5rem;
		opacity: 75%;
	}

	&-btn-col {
		display: grid;
		margin-top: 2.5rem;

		@include media-breakpoint-up(sm) {
			margin-top: 1.5rem;
			display: flex;
		}

		@include media-breakpoint-only(md) {
			display: grid;
			justify-items: flex-end;
		}

		@include media-breakpoint-only(xxl) {
			display: grid;
			justify-items: flex-end;
		}

		.btn {
			@include font-size($font-size-sm);
		}
	}
}
</style>
